<script lang="ts">
	import Action from '../../action/Action.svelte'
	import ShowPassword from '../../form/input/ShowPassword.svelte'
	import { addUrlProtocol } from '../../form/input/addUrlProtocol'
	import Icon from '../../icon/Icon.svelte'
	import { formInputClass } from '../../styles'
	import UrlPreview from '../../urlPreview/UrlPreview.svelte'
	import ErrorLabel from '../errorLabel/ErrorLabel.svelte'
	import { getContextErrors } from '../errors'
	import Label from '../label/Label.svelte'
	import { validateEmail, validateUrl } from '../validations/validations'
	import { InputType } from './input'
	import { _ } from '@repo/i18n'
	import { type IconSource, Pencil } from '@steeze-ui/heroicons'
	import { type Snippet, createEventDispatcher, onDestroy, tick } from 'svelte'
	import { type FullAutoFill } from 'svelte/elements'
	import { createBubbler } from 'svelte/legacy'

	const bubble = createBubbler()

	interface Props {
		value?: string | number
		name?: string
		placeholder?: string
		icon?: IconSource
		label?: string
		unit?: string
		prepend?: string
		required?: boolean
		hideRequired?: boolean
		disabled?: boolean
		min?: number
		max?: number
		step?: number | string
		pattern?: string
		title?: string
		type?: InputType
		autocomplete?: FullAutoFill
		additionalInputClass?: string
		additionalContainerClass?: string
		description?: string | null
		help?: string | null
		tooltip?: string | null
		inputElement?: HTMLInputElement | null
		autofocus?: boolean
		noError?: boolean
		validation?: (value: string) => boolean | null
		validationMessage?: string | null
		lockable?: boolean
		locked?: boolean
		sideLabel?: boolean
		width?: string | null
		helpSlot?: Snippet
		action?: Snippet
		children?: Snippet
	}

	let {
		value = $bindable(),
		name = '',
		placeholder = null,
		icon = null,
		label = '',
		unit = '',
		prepend = '',
		required = false,
		hideRequired = false,
		disabled = false,
		min = 0,
		max = null,
		step = 1,
		pattern = null,
		title = null,
		type = InputType.text,
		autocomplete = null,
		additionalInputClass = '',
		additionalContainerClass = '',
		description = null,
		help = null,
		tooltip = null,
		inputElement = $bindable(null),
		autofocus = false,
		noError = false,
		validation = null,
		validationMessage = null,
		lockable = false,
		locked = $bindable(true),
		sideLabel = false,
		width = null,
		helpSlot,
		action,
		children,
	}: Props = $props()

	const errors = getContextErrors()
	const dispatch = createEventDispatcher()

	let selectionStart: number = null
	let selectionEnd: number = null
	let input: HTMLInputElement = $state(null)

	let isFirstValidation = true

	function handleTextInput(event: Event) {
		selectionStart = (event.target as HTMLInputElement).selectionStart
		selectionEnd = (event.target as HTMLInputElement).selectionEnd

		value = (event.target as HTMLInputElement).value

		if ([InputType.url].includes(type)) {
			value = addUrlProtocol(value)
		}

		// todo: send event instead of value to be consistent across input types
		dispatch('input', value)

		if (!isFirstValidation) {
			validate(value)
		}
	}

	async function handleRestoreFocus() {
		if (selectionStart && selectionEnd) {
			await tick()
			input.focus()
			input.setSelectionRange(selectionStart, selectionEnd)
		}
	}

	export function validate(value: string) {
		if (validation && validationMessage) {
			if (value.length > 0 && !validation(value)) {
				errors.upsert(name, { text: validationMessage })
			} else {
				errors.upsert(name, null)
			}
		} else if (type === InputType.email) {
			if (value.length > 0 && !validateEmail(value)) {
				errors.upsert(name, { text: $_('form.emailInvalid') })
			} else {
				errors.upsert(name, null)
			}
		} else if (type === InputType.url) {
			if (value.length > 0 && !validateUrl(value)) {
				errors.upsert(name, { text: $_('form.urlInvalid') })
			} else {
				errors.upsert(name, null)
			}
		}
	}

	function handleValidation(event: Event) {
		dispatch('blur')

		validate((event.target as HTMLInputElement).value)
		isFirstValidation = false
	}

	onDestroy(() => {
		errors.upsert(name, null)
	})

	const inputClass = `${formInputClass} ${icon ? 'pl-10' : ''} ${unit ? 'pr-12' : ''} ${prepend ? 'pl-16' : ''}`

	let urlPreview = $state(false)

	let isDisabled = $derived(disabled || (lockable && locked))
</script>

<div class={sideLabel ? 'items-center gap-2 sm:flex' : ''}>
	{#if label}
		<Label {name} disabled={isDisabled} {label} required={required && !hideRequired} {tooltip}>
			{#snippet unlock()}
				{#if lockable && locked}
					<Action
						name="lock"
						class="text-primary-500 ml-1"
						ariaLabel={$_('button.edit')}
						icon={Pencil}
						title={$_('button.edit')}
						onclick={() => (locked = false)}
					/>
				{/if}
			{/snippet}
		</Label>
	{/if}

	{#if help || helpSlot}
		<p class="text-sm">
			{#if helpSlot}{@render helpSlot()}{:else}
				{help}
			{/if}
		</p>

		<div class="mt-1"></div>
	{/if}

	<div class="relative flex-none {additionalContainerClass}">
		<div class="relative" class:border-red-700={errors && $errors?.[name]}>
			{#if type === InputType.url && urlPreview}
				{#if typeof value === 'string'}
					<div class="pointer-events-none absolute right-0 flex h-full items-center pr-2">
						<UrlPreview url={value} />
					</div>
				{/if}
			{/if}
			{#if action}
				<div class="absolute right-0 flex h-full items-center pr-2">
					{@render action?.()}
				</div>
			{/if}
			{#if [InputType.text, InputType.email, InputType.url, InputType.tel].includes(type)}
				<!-- svelte-ignore a11y_autofocus -->
				<input
					bind:this={inputElement}
					id={name}
					{name}
					style={width ? 'width: {width}px' : ''}
					class="{inputClass} {additionalInputClass}"
					class:border-red-500={$errors?.[name]}
					class:opacity-50={isDisabled}
					class:outline-red-500={$errors?.[name]}
					{autocomplete}
					{autofocus}
					disabled={isDisabled}
					maxlength={max}
					minlength={min}
					{pattern}
					placeholder={isDisabled ? null : placeholder}
					{required}
					{title}
					type={InputType[type]}
					{value}
					oninput={handleTextInput}
					onblur={(event) => {
						handleValidation(event)
						urlPreview = true
					}}
				/>
			{:else if type === InputType.number}
				<!-- svelte-ignore a11y_autofocus -->
				<input
					id={name}
					{name}
					style={width ? 'width: {width}px' : ''}
					class="{inputClass} {additionalInputClass}"
					class:bg-gray-100={isDisabled}
					class:border-red-500={$errors?.[name]}
					class:opacity-50={isDisabled}
					class:outline-red-500={$errors?.[name]}
					class:text-gray-500={isDisabled}
					{autofocus}
					disabled={isDisabled}
					{max}
					{min}
					{pattern}
					placeholder={isDisabled ? null : placeholder}
					{required}
					{step}
					{title}
					type="number"
					oninput={bubble('input')}
					onblur={bubble('blur')}
					bind:value
				/>
			{:else if type === InputType.password}
				<ShowPassword onChange={handleRestoreFocus}>
					{#snippet children({ type })}
						<input
							bind:this={input}
							id={name}
							{name}
							style={width ? 'width: {width}px' : ''}
							class="{inputClass} {additionalInputClass}"
							class:bg-gray-100={isDisabled}
							class:border-red-500={$errors?.[name]}
							class:outline-red-500={$errors?.[name]}
							class:text-gray-500={isDisabled}
							autocomplete="current-password"
							disabled={isDisabled}
							maxlength={max}
							minlength={min}
							placeholder={isDisabled ? null : placeholder}
							{required}
							type={InputType[type]}
							{value}
							oninput={handleTextInput}
						/>
					{/snippet}
				</ShowPassword>
			{/if}
			{#if icon}
				{#if icon}
					<div
						class="pointer-events-none absolute top-[1px] left-3 flex h-10 items-center text-gray-400"
					>
						<Icon solid src={icon} />
					</div>
				{/if}
			{/if}
			{#if prepend}
				<div
					class="pointer-events-none absolute top-0 left-3 flex h-[42px] items-center text-base text-gray-500"
				>
					{prepend}
				</div>
			{/if}
			{#if unit}
				<div
					class="
            text-primary pointer-events-none absolute top-0 right-3 flex h-[42px] items-center text-base
            {disabled ? 'opacity-50' : ''}
          "
				>
					{unit}
				</div>
			{/if}
		</div>
	</div>

	{#if description}
		<p class="mt-2 text-sm" class:opacity-50={isDisabled}>
			{description}
		</p>
	{/if}

	{@render children?.()}

	{#if !noError}
		<ErrorLabel {name} />
	{/if}
</div>

<style lang="postcss">
	:global(.dark input:-webkit-autofill) {
		box-shadow: 0 0 0 30px var(--color-blue-900) inset;
		-webkit-text-fill-color: var(--color-white);
	}
</style>
